<template>
  <div>
    <dashboard-page-title :showSearch="false" :showFilter="false" :showMainActions="false">
      تفاصيل الرحلة
    </dashboard-page-title>

    <div v-if="loading" class="text-center py-5">
      <b-spinner type="grow" variant="primary" label="Loading..."></b-spinner>
    </div>

    <template v-else-if="trip">
      <b-row>
        <b-col lg="9">
          <!-- Trip Information -->
          <b-card class="mb-4">
            <template v-slot:header>
              <h5 class="text-primary mb-0">معلومات الرحلة</h5>
            </template>
            <b-row>
              <b-col md="4" class="mb-3">
                <input-form
                  label="رقم الرحلة"
                  :value="trip.id != null ? String(trip.id) : '---'"
                  disabled
                />
              </b-col>
              <b-col md="4" class="mb-3">
                <label class="form-label">حالة الرحلة</label>
                <div>
                  <mainstatus :status="getTripStatus()" />
                </div>
              </b-col>
              <b-col md="4" class="mb-3">
                <input-form
                  label="تاريخ ووقت الرحلة"
                  :value="getTripDateTime()"
                  disabled
                />
              </b-col>
              <b-col md="4" class="mb-3">
                <input-form
                  label="المسافة (كم)"
                  :value="trip.distance != null ? String(trip.distance) : '---'"
                  disabled
                />
              </b-col>
              <b-col md="4" class="mb-3">
                <input-form
                  label="إجمالي التكلفة"
                  :value="trip.price != null ? String(trip.price) : '---'"
                  disabled
                />
              </b-col>
              <b-col md="4" class="mb-3">
                <input-form
                  label="عدد الإبل"
                  :value="trip.noOfCamels != null ? String(trip.noOfCamels) : '---'"
                  disabled
                />
              </b-col>
            </b-row>
          </b-card>

          <!-- Route Details -->
          <b-card class="mb-4">
            <template v-slot:header>
              <h5 class="text-primary mb-0">تفاصيل المسار</h5>
            </template>
            <b-row>
              <b-col md="6" class="mb-3">
                <input-form
                  label="عنوان البداية"
                  :value="getStartAddress()"
                  disabled
                />
              </b-col>
              <b-col md="6" class="mb-3">
                <input-form
                  label="عنوان الوصول"
                  :value="getEndAddress()"
                  disabled
                />
              </b-col>
            </b-row>
          </b-card>

          <!-- Vehicle Details -->
          <b-card class="mb-4">
            <template v-slot:header>
              <h5 class="text-primary mb-0">تفاصيل المركبة</h5>
            </template>
            <b-row>
              <b-col md="4" class="mb-3">
                <input-form
                  label="نوع المركبة"
                  :value="getVehicleType()"
                  disabled
                />
              </b-col>
              <b-col md="4" class="mb-3">
                <input-form
                  label="لوحة المركبة"
                  :value="(trip.vehicle && trip.vehicle.plateNumber) || '---'"
                  disabled
                />
              </b-col>
              <b-col md="4" class="mb-3">
                <input-form
                  label="الموديل"
                  :value="(trip.vehicle && trip.vehicle.model) || '---'"
                  disabled
                />
              </b-col>
              <b-col md="4" class="mb-3">
                <input-form
                  label="السنة"
                  :value="(trip.vehicle && trip.vehicle.year) || '---'"
                  disabled
                />
              </b-col>
              <b-col md="4" class="mb-3">
                <input-form
                  label="السعة"
                  :value="getVehicleCapacity()"
                  disabled
                />
              </b-col>
            </b-row>
          </b-card>

          <!-- Driver Details -->
          <b-card class="mb-4">
            <template v-slot:header>
              <h5 class="text-primary mb-0">تفاصيل السائق</h5>
            </template>
            <b-row>
              <b-col md="12" class="mb-3 d-flex align-items-center">
                <b-avatar
                  v-if="getDriverAvatar()"
                  :src="getDriverAvatar()"
                  size="3rem"
                  class="ml-3"
                />
                <b-avatar v-else variant="secondary" size="3rem" class="ml-3">
                  <i class="ri-user-line"></i>
                </b-avatar>
                <div class="flex-grow-1">
                  <input-form
                    label="اسم السائق"
                    :value="(trip.driver && trip.driver.name) || '---'"
                    disabled
                  />
                </div>
              </b-col>
              <b-col md="4" class="mb-3">
                <input-form
                  label="رقم الهاتف"
                  :value="(trip.driver && trip.driver.phone) || '---'"
                  disabled
                />
              </b-col>
              <b-col md="4" class="mb-3">
                <label class="form-label">حالة السائق</label>
                <div>
                  <mainstatus :status="(trip.driver && trip.driver.status) || '---'" />
                </div>
              </b-col>
            </b-row>
            <b-row v-if="hasDriverDocuments()">
              <b-col md="12">
                <label class="form-label">مستندات السائق</label>
                <div class="driver-docs-grid">
                  <div
                    v-for="(doc, index) in getDriverDocuments()"
                    :key="index"
                    class="driver-doc-item"
                  >
                    <div
                      v-if="doc.url"
                      class="driver-doc-preview"
                      @click="showModal({ url: doc.url, title: getDocTitle(doc, index) })"
                    >
                      <img
                        :src="doc.url"
                        :alt="getDocTitle(doc, index)"
                        class="driver-doc-img"
                        @error="handleImageError"
                      />
                      <div class="driver-doc-overlay">
                        <i class="ri-zoom-in-line"></i>
                        <span>عرض</span>
                      </div>
                    </div>
                    <div v-else class="driver-doc-placeholder">
                      <i class="ri-file-line"></i>
                      <span>{{ getDocTitle(doc, index) }}</span>
                    </div>
                  </div>
                </div>
              </b-col>
            </b-row>
          </b-card>

          <!-- Notes -->
          <b-card v-if="getNotes()" class="mb-4">
            <template v-slot:header>
              <h5 class="text-primary mb-0">الملاحظات</h5>
            </template>
            <p class="mb-0">{{ getNotes() }}</p>
          </b-card>
        </b-col>

        <b-col md="3" v-if="getVehicleImage()">
          <b-card class="mb-4 vehicle-image-card">
            <template v-slot:header>
              <h5 class="text-primary mb-0">صورة المركبة</h5>
            </template>
            <div
              class="vehicle-image-wrapper"
              :class="{ 'vehicle-image-wrapper--error': vehicleImageError }"
              @click="onVehicleImageClick"
            >
              <img
                v-show="!vehicleImageError"
                :src="getVehicleImage()"
                alt="صورة المركبة"
                class="vehicle-image"
                @error="vehicleImageError = true"
              />
              <div v-show="vehicleImageError" class="vehicle-image-placeholder">
                <i class="ri-image-line"></i>
                <span>لا تتوفر صورة</span>
              </div>
              <div v-show="!vehicleImageError" class="vehicle-image-overlay">
                <i class="ri-zoom-in-line"></i>
                <span>تكبير</span>
              </div>
            </div>
          </b-card>
        </b-col>
      </b-row>

      <!-- Image/Document Modal -->
      <b-modal
        ref="modal-photo"
        id="modal-photo"
        size="xl"
        centered
        hide-footer
        content-class="image-preview-modal"
      >
        <template #modal-title>
          {{ selectedPhoto.title }}
        </template>
        <div class="modal-image-container">
          <img :src="selectedPhoto.url" class="modal-image" alt="" @error="handleModalImageError" />
        </div>
      </b-modal>
    </template>

    <b-alert v-else-if="!loading && error" variant="danger" show>
      {{ error }}
    </b-alert>
  </div>
</template>

<script>
import { core } from '@/config/pluginInit'
import api from '../services/transportation'
import mainstatus from '@/components/table/status'
import inputForm from '@/components/formElements/InputForm'

export default {
  name: 'showTripDetails',
  components: {
    mainstatus,
    inputForm
  },
  data () {
    return {
      trip: null,
      loading: true,
      error: null,
      selectedPhoto: { url: '', title: '' },
      vehicleImageError: false
    }
  },
  methods: {
    getTripDetails () {
      this.loading = true
      this.error = null
      api
        .getTrip(this.$route.params.id)
        .then((res) => {
          this.trip = res.data
          this.vehicleImageError = false
        })
        .catch((err) => {
          const msg = err && err.response && err.response.data && err.response.data.message
          this.error = msg || 'فشل تحميل تفاصيل الرحلة'
        })
        .finally(() => {
          this.loading = false
        })
    },
    formatDateTime (value) {
      if (!value) return '---'
      if (typeof value === 'object' && Object.keys(value).length === 0) return '---'
      if (typeof value === 'string' && value.length > 10) {
        return new Date(value).toLocaleString('ar-EG')
      }
      return value
    },
    getTripStatus () {
      const t = this.trip
      if (!t) return '---'
      return (t.tripRequest && t.tripRequest.status) || t.status || '---'
    },
    getTripDateTime () {
      const t = this.trip
      if (!t) return '---'
      const dt = (t.tripRequest && t.tripRequest.dateTime) || t.dateTime
      return this.formatDateTime(dt)
    },
    getStartAddress () {
      const t = this.trip
      if (!t) return '---'
      return (t.tripRequest && t.tripRequest.startAddress) || t.startAddress || (t.route && t.route.startAddress) || t.start_address || '---'
    },
    getEndAddress () {
      const t = this.trip
      if (!t) return '---'
      return (t.tripRequest && t.tripRequest.endAddress) || t.endAddress || (t.route && t.route.endAddress) || t.end_address || '---'
    },
    getVehicleType () {
      const v = this.trip && this.trip.vehicle
      if (!v) return '---'
      const vt = v.vehicleType
      if (vt && vt.name) return vt.name
      if (vt && vt.translations && vt.translations[0] && vt.translations[0].name) return vt.translations[0].name
      return v.type || '---'
    },
    getVehicleImage () {
      const v = this.trip && this.trip.vehicle
      if (!v) return null
      if (v.featuredImage) return v.featuredImage
      if (v.images) {
        try {
          const imgs = typeof v.images === 'string' ? JSON.parse(v.images) : v.images
          return Array.isArray(imgs) && imgs[0] ? imgs[0] : null
        } catch (e) {
          return v.images
        }
      }
      return v.image || v.imageUrl || v.photo || null
    },
    getVehicleCapacity () {
      const t = this.trip
      if (!t) return '---'
      const vt = (t.vehicle && t.vehicle.vehicleType) || t.vehicleType
      if (!vt) return '---'
      const cap = vt.capacity
      return cap != null ? String(cap) : '---'
    },
    getVehicleTypeImage () {
      const t = this.trip
      if (!t) return null
      const vt = (t.vehicle && t.vehicle.vehicleType) || t.vehicleType
      if (!vt) return null
      return vt.image || vt.logo || null
    },
    getDriverAvatar () {
      const d = this.trip && this.trip.driver
      if (!d) return null
      return d.avatar || d.image || d.imageUrl || d.photo || null
    },
    getDriverDocuments () {
      const d = this.trip && this.trip.driver
      const docs = (d && d.driverDocs) || (d && d.documents) || []
      return Array.isArray(docs) ? docs : []
    },
    getDocTitle (doc, index) {
      if (!doc) return 'مستند ' + (index + 1)
      return doc.name || (doc.document && doc.document.name) || 'مستند ' + (index + 1)
    },
    hasDriverDocuments () {
      return this.getDriverDocuments().length > 0
    },
    getNotes () {
      const t = this.trip
      return (t && t.notes) || (t && t.note) || null
    },
    onVehicleImageClick () {
      if (!this.vehicleImageError && this.getVehicleImage()) {
        this.showModal({ url: this.getVehicleImage(), title: 'صورة المركبة' })
      }
    },
    showModal (item) {
      this.selectedPhoto = { url: item.url, title: item.title }
      const modal = this.$refs['modal-photo']
      if (modal && modal.show) {
        modal.show()
      }
    },
    handleImageError (e) {
      e.target.style.display = 'none'
      const parent = e.target.parentElement
      if (parent && !parent.querySelector('.driver-doc-placeholder')) {
        const placeholder = document.createElement('div')
        placeholder.className = 'driver-doc-placeholder'
        placeholder.innerHTML = '<i class="ri-file-damage-line"></i><span>لا يمكن عرض الصورة</span>'
        parent.appendChild(placeholder)
      }
    },
    handleModalImageError (e) {
      e.target.style.display = 'none'
      const container = e.target.parentElement
      if (container && !container.querySelector('.modal-image-error')) {
        const msg = document.createElement('p')
        msg.className = 'modal-image-error text-muted text-center py-5 mb-0'
        msg.textContent = 'لا يمكن تحميل الصورة'
        container.appendChild(msg)
      }
    }
  },
  created () {
    if (this.$route.params.id) {
      this.getTripDetails()
    }
  },
  mounted () {
    core.index()
  }
}
</script>

<style scoped>
.vehicle-image-card {
  position: sticky;
  top: 1rem;
}
.vehicle-image-wrapper {
  position: relative;
  border-radius: 8px;
  overflow: hidden;
  background: #f8f9fa;
  cursor: pointer;
  aspect-ratio: 4/3;
}
.vehicle-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  transition: transform 0.3s ease;
}
.vehicle-image-wrapper:hover .vehicle-image {
  transform: scale(1.05);
}
.vehicle-image-overlay {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.4);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: white;
  opacity: 0;
  transition: opacity 0.3s ease;
}
.vehicle-image-wrapper:hover .vehicle-image-overlay {
  opacity: 1;
}
.vehicle-image-wrapper--error {
  cursor: default;
}
.vehicle-image-overlay i {
  font-size: 2rem;
  margin-bottom: 0.25rem;
}
.vehicle-image-placeholder {
  aspect-ratio: 4/3;
  background: #f8f9fa;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #6c757d;
}
.vehicle-image-placeholder i {
  font-size: 3rem;
  margin-bottom: 0.5rem;
}

.driver-docs-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
  gap: 1rem;
}
.driver-doc-item {
  min-height: 100px;
}
.driver-doc-preview {
  position: relative;
  border-radius: 8px;
  overflow: hidden;
  background: #f8f9fa;
  cursor: pointer;
  aspect-ratio: 1;
  border: 2px solid #e9ecef;
  transition: all 0.2s ease;
}
.driver-doc-preview:hover {
  border-color: var(--primary, #0084ff);
  box-shadow: 0 4px 12px rgba(0, 132, 255, 0.2);
}
.driver-doc-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}
.driver-doc-overlay {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: white;
  opacity: 0;
  transition: opacity 0.2s ease;
  font-size: 0.85rem;
}
.driver-doc-preview:hover .driver-doc-overlay {
  opacity: 1;
}
.driver-doc-overlay i {
  font-size: 1.5rem;
  margin-bottom: 0.25rem;
}
.driver-doc-placeholder {
  aspect-ratio: 1;
  background: #f8f9fa;
  border: 2px dashed #dee2e6;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #6c757d;
  font-size: 0.8rem;
  text-align: center;
  padding: 0.5rem;
}
.driver-doc-placeholder i {
  font-size: 1.5rem;
  margin-bottom: 0.25rem;
}
</style>
